import { z } from 'zod';

const moneyDto = z.object({
  amount: z.number(),
  currency: z.enum(['GEL', 'USD', 'EUR']),
});

const imageDto = z.object({
  url: z.string().url(),
  alt: z.string().default(''),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

export const productSummaryDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  sku: z.string().min(1),
  meta_catalog: z.object({ content_id: z.string().optional(), content_brand: z.string(), product_type: z.string(), content_category: z.string() }).optional(),
  slug: z.string().min(1),
  title: z.string(),
  brand: z.string().nullable().default(null),
  facts: z.array(z.object({ key: z.string(), value: z.string() })).default([]),
  category_slug: z.string().nullable().default(null),
  price: moneyDto,
  compare_at_price: moneyDto.nullable().default(null),
  image: imageDto.nullable().default(null),
  in_stock: z.boolean(),
  rating: z.number().min(0).max(5).nullable().default(null),
  review_count: z.number().int().min(0).default(0),
  offer: z.object({
    id: z.string(), title: z.string(), description: z.string(),
    type: z.enum(['quantity', 'product']),
    required_quantity: z.number().int().positive(),
    discount_type: z.enum(['percent', 'fixed']),
    discount_value: z.number().positive(),
    ends_at: z.string().nullable(),
  }).nullable().default(null),
  promotion_ids: z.array(z.string()).optional(),
});

export const productDetailDto = productSummaryDto.extend({
  meta_title: z.string().nullable().default(null),
  description: z.string().default(''),
  images: z.array(imageDto).default([]),
  specs: z.array(z.object({ key: z.string(), value: z.string() })).default([]),
  variants: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]).transform(String),
        slug: z.string(),
        label: z.string(),
        in_stock: z.boolean(),
      }),
    )
    .default([]),
  color_variations: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]).transform(String),
        slug: z.string(),
        label: z.string(),
        color: z.string().nullable(),
        in_stock: z.boolean(),
      }),
    )
    .default([]),
  stock_count: z.number().int().min(0).nullable().default(null),
  categories: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]).transform(String),
        slug: z.string(),
        name: z.string().default(''),
      }),
    )
    .default([]),
});

export const productListDto = z.object({
  data: z.array(productSummaryDto),
  meta: z.object({
    current_page: z.number().int().positive(),
    per_page: z.number().int().positive(),
    total: z.number().int().min(0),
    last_page: z.number().int().min(0),
    promotion_filter_id: z.string().nullable().optional(),
  }),
});

export const productFiltersDto = z.object({
  data: z.object({
    price_range: z.object({
      min: z.number().nullable(),
      max: z.number().nullable(),
    }),
    attributes: z.array(
      z.object({
        id: z.union([z.string(), z.number()]).transform(String),
        name: z.string(),
        code: z.string(),
        type: z.string(),
        options: z.array(
          z.object({
            id: z.union([z.string(), z.number()]).transform(String),
            label: z.string(),
            color: z.string().nullable(),
            value: z.string().nullable(),
            count: z.number().int().min(0),
          }),
        ),
      }),
    ),
    categories: z
      .array(
        z.object({
          id: z.union([z.string(), z.number()]).transform(String),
          slug: z.string(),
          name: z.string(),
          count: z.number().int().min(0),
        }),
      )
      .default([]),
  }),
});

export type ProductSummaryDto = z.infer<typeof productSummaryDto>;
export type ProductDetailDto = z.infer<typeof productDetailDto>;
export type ProductListDto = z.infer<typeof productListDto>;
export type ProductFiltersDto = z.infer<typeof productFiltersDto>;
